import type { ComponentProps } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { rem } from 'polished'
import moment from 'moment'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { INTL_SHORT_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { useInlineListFormatter } from 'hooks/useListFormatters'

import { CruiseDurationOptions } from './Inputs/CruiseWhenSelect/CruiseWhenSelectHowLong'
import useCruiseDurationLabels from 'hooks/Cruise/useCruiseDurationLabels'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { take } from 'lib/array/arrayUtils'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import Clickable from 'components/Common/Clickable'
import zIndex from 'styles/tools/z-index'
import clsx from 'clsx'
import { SearchMenuStates } from 'components/Search/type'
import useScrollDirection from 'hooks/useScrollDirection'
import { useInView } from 'react-intersection-observer'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CruiseHeaderFilterChip from 'components/Cruises/SearchPage/Filters/CruiseHeaderFilterChip'
import CruiseSelectCabinType from './CruiseSelectCabinType'
import Divider from 'LuxKit/Divider'
import Sheet from 'components/utils/Sheet'

const SectionContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  position: sticky;
  top: 0;
  margin-top: 0;
  transition: top 1s ease-in-out;
  z-index: ${zIndex.searchMenu};

  &.offScreen {
    width: 100%;
    top: -50%;
    padding: ${rem(12)};
    box-shadow: ${props => props.theme.shadow.bottom.small};
    transition: top 1s ease-in-out;
  }
  &.lifted {
    top: 0;
    padding: ${rem(12)};
    transition: top 0.5s ease-out;
    z-index: ${zIndex.stickySubHeader};
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const SearchContainer = styled(VerticalSpacer)`
  padding: ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} 0;
  }
`

const EditGroup = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding: ${rem(8)};
  width: 100%;
`
type InheritedCruiseHeaderFilterChipProps = Pick<ComponentProps<typeof CruiseHeaderFilterChip>, 'onSortChange'>

interface Props extends InheritedCruiseHeaderFilterChipProps {
  filters: App.OfferListFilters
  className?: string
}

function CruiseSearchFiltersMobile(props: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const formatUnitList = useInlineListFormatter('unit')
  const durationLabels = useCruiseDurationLabels()
  const { filters, onSortChange } = props
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const openCruisesSearch = useCallback(() => { toggleSearchVertical('cruises') }, [toggleSearchVertical])
  const [cruiseLines] = useCruiseSearchFacets({ facetTypes: ['cruise_lines'] })

  const {
    departureMonths: flexibleMonths,
    durationRange,
  } = filters

  const formatSummaryWithAdditionalCount = useCallback(
    (values?: Array<string>, limitVisible = 1) => {
      if (!values?.length) {
        return ''
      }

      const visibleValues = take(values, limitVisible)
      const visibleValuesText =
        visibleValues.length > 1 ? formatUnitList(visibleValues) : visibleValues[0]

      if (values.length > limitVisible) {
        return intl.formatMessage(
          {
            id: 'tgImrg9_XDzzV75GhKcL',
            defaultMessage: '{visibleValues} +{additionalCount}',
            description:
              'Cruise mobile filter summary showing the first selected values followed by the count of additional selections',
          },
          {
            visibleValues: visibleValuesText,
            additionalCount: values.length - limitVisible,
          },
        )
      }

      return visibleValuesText
    },
    [formatUnitList, intl],
  )

  const formatSummaryParts = useCallback(
    (parts: Array<string>) => {
      const visibleParts = parts.filter(Boolean)

      if (visibleParts.length === 0) {
        return ''
      }

      if (visibleParts.length === 1) {
        return visibleParts[0]
      }

      if (visibleParts.length === 2) {
        return intl.formatMessage(
          {
            id: 'Kjivc1PAngfqkhbsOTwJ',
            defaultMessage: '{firstPart}. {secondPart}',
            description:
              'Two-part cruise mobile filter summary combining selected search filters into one sentence',
          },
          {
            firstPart: visibleParts[0],
            secondPart: visibleParts[1],
          },
        )
      }

      return intl.formatMessage(
        {
          id: '3JetPDDDrFIXvC1aYh74',
          defaultMessage: '{firstPart}. {secondPart}. {thirdPart}',
          description:
            'Three-part cruise mobile filter summary combining selected search filters into one sentence',
        },
        {
          firstPart: visibleParts[0],
          secondPart: visibleParts[1],
          thirdPart: visibleParts[2],
        },
      )
    },
    [intl],
  )

  const firstLineFilters = useMemo(() => {
    const destinationName = formatSummaryWithAdditionalCount(filters.destinationNames)
    const departureName = formatSummaryWithAdditionalCount(filters.departureNames)
    const data: Array<string> = []

    data.push(departureName ? intl.formatMessage({
      id: 'fGXbVOcmmee5FgmzchyG',
      defaultMessage: 'Starts in {departureName}',
      description: 'Cruise departure port filter label showing the selected port name',
    }, { departureName }) : intl.formatMessage({
      id: 'WFqg1ytOT4nTRZZlRR_n',
      defaultMessage: 'All departure ports',
      description: 'Default label when no departure port is selected in cruise search filters',
    }))
    data.push(destinationName ? intl.formatMessage({
      id: 'qXmTfSyJGJprnD85eK_b',
      defaultMessage: 'Cruising to {destinationName}',
      description: 'Cruise destination filter label showing the selected destination name',
    }, { destinationName }) : intl.formatMessage({
      id: 'ZrQ1JWx7ZBGcRNlMKdJd',
      defaultMessage: 'All destinations',
      description: 'Default label when no destination is selected in cruise search filters',
    }))

    return formatSummaryParts(data)
  }, [
    filters.destinationNames,
    filters.departureNames,
    formatSummaryParts,
    formatSummaryWithAdditionalCount,
    intl,
  ])

  const secondLineFilters = useMemo(() => {
    const data: Array<string> = []

    const allCruiseLineNames = cruiseLines?.filter(cruiseLine => filters.cruiseLines?.includes(cruiseLine.value ?? ''))
      .map(cruiseLine => cruiseLine.name)

    if (allCruiseLineNames.length > 0) {
      data.push(formatSummaryWithAdditionalCount(allCruiseLineNames))
    } else {
      data.push(intl.formatMessage({
        id: 'EUV73nbFCBbuoQTAFLtq',
        defaultMessage: 'All cruise lines',
        description: 'Default label when no cruise line is selected in cruise search filters',
      }))
    }

    const durations = CruiseDurationOptions.filter(opt => durationRange?.includes(opt.duration))
    const valueDuration = formatUnitList(
      durations.map(value => durationLabels[value.name] ?? value.name),
    )
    data.push(valueDuration || intl.formatMessage({
      id: 'omxrQnTfuio8FKe3vHTq',
      defaultMessage: 'Any duration',
      description: 'Default label when no cruise duration is selected in cruise search filters',
    }))

    const departureMonths = flexibleMonths?.filter(Boolean) ?? []
    if (departureMonths.length) {
      const formattedMonths = departureMonths.map(month =>
        intlDateFormatter(moment(month, 'YYYY-MM'), INTL_SHORT_MONTH_LONG_YEAR),
      )
      const dateValues = formatSummaryWithAdditionalCount(formattedMonths)

      if (dateValues) {
        data.push(dateValues)
      }
    }

    return formatSummaryParts(data)
  }, [
    cruiseLines,
    durationRange,
    filters.cruiseLines,
    flexibleMonths,
    formatSummaryParts,
    formatSummaryWithAdditionalCount,
    formatUnitList,
    intl,
    intlDateFormatter,
    durationLabels,
  ])

  const {
    activeMenu,
  } = useContext(GlobalSearchStateContext)
  const scrollDirection = useScrollDirection({ tolerance: 20 })
  const [toLiftRef, liftInView] = useInView()
  const isLifted = !liftInView && scrollDirection === 'up'
  const isOffScreen = !liftInView && scrollDirection !== 'up'

  return <>
    <SectionContainer
      className={clsx({
        lifted: isLifted || activeMenu !== SearchMenuStates.Closed,
        offScreen: isOffScreen && activeMenu === SearchMenuStates.Closed,
      })}
    >
      <CSSBreakpoint max="mobile">
        <Group direction="vertical" gap={12}>
          <Sheet padding="0 16">
            <Group direction="vertical" gap={12}>
              <Clickable onClick={openCruisesSearch}>
                <EditGroup>
                  <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between">
                    <Group direction="vertical" verticalAlign="center">
                      <Heading variant="heading6">{firstLineFilters}</Heading>
                      <Caption variant="large">
                        {secondLineFilters}
                      </Caption>
                    </Group>
                    <LineSearchIcon size={20} />
                  </Group>
                </EditGroup>
              </Clickable>

              <CruiseHeaderFilterChip
                filters={filters}
                onSortChange={onSortChange}
              />
            </Group>
          </Sheet>
          <Divider kind="primary" className="divider" />
          <Sheet padding="0 16">
            <CruiseSelectCabinType filters={filters} isMobile />
          </Sheet>
        </Group>
      </CSSBreakpoint>
      <CSSBreakpoint min="desktop">
        <SearchContainer gap={12}>
          <Group direction="horizontal" horizontalAlign="space-between" gap={12} verticalAlign="center">
            <VerticalSpacer gap={4}>
              <BodyText variant="medium" weight="bold">
                {firstLineFilters}
              </BodyText>
              <BodyText variant="medium" colour="neutral-two">
                {secondLineFilters}
              </BodyText>
            </VerticalSpacer>
            <TextLink size="medium" onClick={openCruisesSearch}>{intl.formatMessage({
              id: 'hRKLoN1MnyJ1TYqWyx-t',
              defaultMessage: 'Edit',
              description: 'Link text to edit cruise search filters in the cruise offer or booking section',
            })}
            </TextLink>
          </Group>
          <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<LineFilterAltIcon />}
            fit="full-width"
            size="small"
          >
            {intl.formatMessage({
              id: 'cCGbD6CsoPMvKCjNNy6V',
              defaultMessage: 'Sort & filter',
              description: 'Button text to open the cruise sort and filter options',
            })}
          </TextButton>
        </SearchContainer>
      </CSSBreakpoint>
    </SectionContainer>
    <div ref={toLiftRef} />
  </>
}

export default CruiseSearchFiltersMobile
